import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Frame, PencilRuler, Sofa, type LucideIcon } from "lucide-react";
import { isUnstartedRoom, useDesign } from "../lib/store";
import type { DesignerView } from "../lib/types";
import { useIsMobile, useLayoutMode } from "../lib/useViewportSize";
import { cn } from "../lib/cn";

const TABS: readonly { id: DesignerView; label: string; icon: LucideIcon }[] = [
  { id: "build", label: "Shape", icon: PencilRuler },
  { id: "design", label: "Design", icon: Sofa },
  { id: "wall", label: "Walls", icon: Frame },
];

/**
 * Mode switcher for phones — the thumb-reachable replacement for the
 * top-bar segmented control. Portrait: a bottom bar whose height is
 * `--modebar-h` (56px plus the home-indicator inset), which every view's
 * bottom offsets stack on. Landscape: a rail on the left edge,
 * `--rail-w` wide, so the short viewport keeps its full height for the
 * canvas; views start after it.
 *
 * Design and Walls need a room outline first. Those tabs stay tappable so
 * a tap can explain why instead of silently doing nothing.
 */
export function MobileModeBar() {
  const isMobile = useIsMobile();
  const rail = useLayoutMode() === "phoneLandscape";
  const view = useDesign((s) => s.view);
  const setView = useDesign((s) => s.setView);
  const roomUnstarted = useDesign((s) => isUnstartedRoom(s.design.room));
  const [hint, setHint] = useState<string | null>(null);
  const hintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (hintTimer.current) clearTimeout(hintTimer.current);
  }, []);

  if (!isMobile) return null;

  function choose(id: DesignerView) {
    if (id !== "build" && roomUnstarted) {
      setHint("Sketch your room or pick a shape first.");
      if (hintTimer.current) clearTimeout(hintTimer.current);
      hintTimer.current = setTimeout(() => setHint(null), 2600);
      return;
    }
    setHint(null);
    setView(id);
  }

  return (
    <nav
      aria-label="Design mode"
      data-testid="mobile-mode-bar"
      data-orientation={rail ? "vertical" : "horizontal"}
      className={cn(
        "absolute z-30 bg-white/95 backdrop-blur-md",
        rail
          ? "left-0 top-[var(--topbar-h)] bottom-0 w-[var(--rail-w)] border-r border-ink-200/80 pl-[env(safe-area-inset-left)] pb-[env(safe-area-inset-bottom)]"
          : "inset-x-0 bottom-0 h-[var(--modebar-h)] border-t border-ink-200/80 pb-[env(safe-area-inset-bottom)]"
      )}
    >
      <AnimatePresence>
        {hint && (
          <motion.div
            role="status"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "pointer-events-none absolute whitespace-nowrap rounded-full bg-ink-900 px-4 py-2 text-body-sm font-medium text-white shadow-lg",
              rail ? "left-full top-3 ml-3" : "bottom-full left-1/2 mb-3 -translate-x-1/2"
            )}
          >
            {hint}
          </motion.div>
        )}
      </AnimatePresence>
      <div className={cn(rail ? "flex flex-col gap-1 pt-2" : "mx-auto grid h-14 max-w-lg grid-cols-3")}>
        {TABS.map((tab) => {
          const active = view === tab.id;
          const locked = tab.id !== "build" && roomUnstarted;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => choose(tab.id)}
              aria-current={active ? "page" : undefined}
              aria-disabled={locked || undefined}
              data-testid={`mode-tab-${tab.id}`}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 active:bg-ink-50",
                rail && "h-14"
              )}
            >
              <span
                className={cn(
                  "flex h-7 w-14 items-center justify-center rounded-full transition-colors",
                  active ? "bg-ink-900 text-white" : "text-ink-500",
                  locked && "opacity-40"
                )}
              >
                <Icon className="size-[18px]" aria-hidden />
              </span>
              <span
                className={cn(
                  "text-xs leading-none",
                  active ? "font-semibold text-ink-900" : "font-medium text-ink-500",
                  locked && "opacity-40"
                )}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
